// FEEL lexer — covers the subset of DMN 1.3 FEEL needed by decision tables,
// literal expressions, and unary tests. Not a full spec implementation: no
// date/time/duration literals (those come from built-ins), no multi-word
// keywords like `some … in …` (the parser recognises identifiers `some`
// contextually).

export const T = Object.freeze({
  NUMBER:  'NUMBER',
  STRING:  'STRING',
  BOOLEAN: 'BOOLEAN',
  NULL:    'NULL',
  IDENT:   'IDENT',
  KEYWORD: 'KEYWORD',
  PUNCT:   'PUNCT',
  OP:      'OP',
  DASH:    'DASH',
  EOF:     'EOF',
});

const KEYWORDS = new Set([
  'true', 'false', 'null',
  'and', 'or', 'not',
  'if', 'then', 'else',
  'for', 'in', 'return',
  'some', 'every', 'satisfies',
  'between',
]);

// Multi-char operators are matched first.
const OPS_MULTI = ['**', '<=', '>=', '!=', '..'];
const OPS_SINGLE = ['+', '-', '*', '/', '=', '<', '>'];
const PUNCTS = ['(', ')', '[', ']', '{', '}', ',', '.', ':', ';'];

export class FeelLexError extends Error {
  constructor(msg, pos) { super(`[feel/lex] ${msg} at ${pos}`); this.pos = pos; }
}

export function tokenize(source) {
  const tokens = [];
  let i = 0;
  const len = source.length;

  const push = (type, value, start, end) => tokens.push({ type, value, start, end });
  const isDigit = (c) => c >= '0' && c <= '9';
  const isIdStart = (c) => /[A-Za-z_]/.test(c);
  const isIdCont = (c) => /[A-Za-z0-9_]/.test(c);

  while (i < len) {
    const c = source[i];

    // whitespace
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { i++; continue; }

    // line comment — not in spec but handy
    if (c === '/' && source[i + 1] === '/') {
      while (i < len && source[i] !== '\n') i++;
      continue;
    }

    // string literal (double-quoted). Escapes: \" \\ \n \t
    if (c === '"') {
      const start = i++;
      let out = '';
      while (i < len && source[i] !== '"') {
        if (source[i] === '\\' && i + 1 < len) {
          const n = source[i + 1];
          const map = { '"': '"', '\\': '\\', 'n': '\n', 't': '\t', 'r': '\r' };
          out += map[n] != null ? map[n] : n;
          i += 2;
        } else {
          out += source[i++];
        }
      }
      if (source[i] !== '"') throw new FeelLexError('unterminated string', start);
      i++;
      push(T.STRING, out, start, i);
      continue;
    }

    // number (decimal, optional fractional, no scientific for now — can add)
    if (isDigit(c) || (c === '.' && isDigit(source[i + 1]))) {
      const start = i;
      while (i < len && isDigit(source[i])) i++;
      if (source[i] === '.' && isDigit(source[i + 1])) {
        i++;
        while (i < len && isDigit(source[i])) i++;
      }
      // scientific notation 1e9 / 1E-9
      if (source[i] === 'e' || source[i] === 'E') {
        i++;
        if (source[i] === '+' || source[i] === '-') i++;
        while (i < len && isDigit(source[i])) i++;
      }
      const text = source.slice(start, i);
      push(T.NUMBER, Number(text), start, i);
      continue;
    }

    // identifier / keyword
    if (isIdStart(c)) {
      const start = i++;
      while (i < len && isIdCont(source[i])) i++;
      const text = source.slice(start, i);
      if (text === 'true' || text === 'false')      push(T.BOOLEAN, text === 'true', start, i);
      else if (text === 'null')                      push(T.NULL, null, start, i);
      else if (KEYWORDS.has(text))                   push(T.KEYWORD, text, start, i);
      else                                           push(T.IDENT, text, start, i);
      continue;
    }

    // multi-char operators
    let matched = false;
    for (const op of OPS_MULTI) {
      if (source.startsWith(op, i)) {
        push(T.OP, op, i, i + op.length);
        i += op.length;
        matched = true;
        break;
      }
    }
    if (matched) continue;

    // single-char operators
    if (OPS_SINGLE.includes(c)) { push(T.OP, c, i, i + 1); i++; continue; }

    // punctuation
    if (PUNCTS.includes(c)) { push(T.PUNCT, c, i, i + 1); i++; continue; }

    throw new FeelLexError(`unexpected character '${c}'`, i);
  }

  push(T.EOF, null, len, len);
  return tokens;
}
